import React from 'react';
const { useState, useEffect } = React;
import { inputSt, mutedSt, badgeSt, warnSt, fmtDate } from '../diagnostic/shared.jsx';
import { coachCall, copy } from './shared.jsx';
import { getAuthHeaders } from '../../globals.js';

// The link between a commercial contact and a coaching person, seen by the
// coach only (coaching-admin checks it on every call). It carries basic data
// only: names, emails, process names. Matches by email are suggestions; the
// coach decides, can change or remove the link, and every change is kept.

/** Opens another section of the app (App.jsx listens). */
export const navigate = (detail) => window.dispatchEvent(new CustomEvent('oike:navigate', { detail }));

/** Search a contact (used when creating a person, or to link an existing one). */
export function ContactPicker({ onPick, suggestions = [], disabled }) {
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (q.trim().length < 2) { setList(null); return; }
    const t = setTimeout(() => coachCall({ action: 'contactSearch', q }).then(d => setList(d.contacts)).catch(e => setError(e.message)), 250);
    return () => clearTimeout(t);
  }, [q]);
  const row = (c, why) => (
    <button key={c.id} type="button" className="action-btn" disabled={disabled} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', textAlign: 'left', fontSize: 12, gap: 8 }} onClick={() => onPick(c)}>
      <span><b>{c.name}</b>{c.role ? ` · ${c.role}` : ''}</span><span style={mutedSt}>{c.email || 'sin email'}{why ? ` · ${why}` : ''}</span>
    </button>
  );
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {suggestions.length > 0 && <div style={{ display: 'grid', gap: 4 }}><span style={mutedSt}>Posibles coincidencias (mismo email): revisa antes de vincular.</span>{suggestions.map(c => row(c, 'mismo email'))}</div>}
      <input type="search" style={inputSt} value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar un contacto por nombre o email…" aria-label="Buscar contacto" />
      {error && <div style={{ ...warnSt, padding: 6 }}>{error}</div>}
      {list && (!list.length ? <span style={mutedSt}>No hay contactos con ese nombre o email.</span> : <div style={{ display: 'grid', gap: 4 }}>{list.map(c => row(c))}</div>)}
    </div>
  );
}

/** In the People tab: the contact of one person (link, change, remove, history). */
export function PersonContact({ person, suggestions, onChanged }) {
  // The contact loaded for one link: right after linking or changing it, the
  // previous one (or none) must not be shown while the new one loads
  const [loaded, setLoaded] = useState({ id: null, contact: undefined });
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  useEffect(() => {
    const id = person.stakeholderId;
    if (!id) return;
    let alive = true;
    coachCall({ action: 'personContact', personId: person.id })
      .then(d => { if (alive) setLoaded({ id, contact: d.contact || null }); })
      .catch(() => { if (alive) setLoaded({ id, contact: null }); });
    return () => { alive = false; };
  }, [person.id, person.stakeholderId]);
  const contact = loaded.id === person.stakeholderId ? loaded.contact : undefined;
  const link = async (stakeholderId) => {
    setError('');
    try { await coachCall({ action: 'linkContact', personId: person.id, stakeholderId, note }); setEditing(false); setNote(''); onChanged(); } catch (e) { setError(e.message); }
  };
  const sugg = suggestions?.suggestions || [];
  return (
    <div style={{ fontSize: 12, display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={mutedSt}>Contacto:</span>
        {!person.stakeholderId ? <span style={mutedSt}>sin vincular{sugg.length ? ` · ${sugg.length} posible${sugg.length === 1 ? '' : 's'} coincidencia${sugg.length === 1 ? '' : 's'}` : ''}</span>
          : contact === undefined ? <span style={mutedSt}>…</span>
          : contact === null ? <span style={{ color: '#9A6212' }}>no se pudo cargar el contacto; recarga para verlo</span>
          : contact.missing ? <span style={{ color: '#9A6212' }}>el contacto vinculado ya no existe</span>
          : <button type="button" className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => navigate({ page: 'contacts', contactId: contact.id })}>{contact.name} ↗</button>}
        {suggestions?.emailDiffers && <span style={{ ...badgeSt(false), color: '#9A6212' }}>el email del contacto es distinto</span>}
        <button type="button" className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setEditing(!editing)}>{person.stakeholderId ? 'Cambiar' : 'Vincular'}</button>
        {person.stakeholderId && <button type="button" className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => window.confirm('¿Quitar el vínculo con este contacto? La persona y el contacto no cambian; queda en el historial.') && link(null)}>Quitar vínculo</button>}
        {person.linkHistory?.length > 0 && <button type="button" className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setShowHistory(!showHistory)}>Historial</button>}
      </div>
      {editing && (
        <div style={{ display: 'grid', gap: 6, padding: 8, border: '1px solid var(--globant-border)', borderRadius: 8 }}>
          <input style={inputSt} value={note} maxLength={300} onChange={e => setNote(e.target.value)} placeholder="Nota del cambio (opcional)" aria-label="Nota del cambio" />
          <ContactPicker suggestions={sugg} onPick={(c) => link(c.id)} />
          <button type="button" className="action-btn" style={{ justifySelf: 'start', fontSize: 11 }} onClick={() => setEditing(false)}>Cancelar</button>
        </div>
      )}
      {showHistory && <div style={{ ...mutedSt, display: 'grid', gap: 2 }}>{person.linkHistory.map((h, i) => <div key={i}>{fmtDate(h.at, true)} · {h.to ? (h.from ? 'cambió el contacto' : 'vinculó un contacto') : 'quitó el vínculo'}{h.by ? ` · ${h.by}` : ''}{h.note ? ` · “${h.note}”` : ''}</div>)}</div>}
      {error && <div style={{ ...warnSt, padding: 6 }}>{error}</div>}
    </div>
  );
}

/**
 * In the contact card (commercial side): the coaching person and processes of
 * this contact. Only for the coach: anyone else gets 403 from the server and
 * this renders nothing. Names and statuses only.
 */
export function ContactCoachingPanel({ stakeholderId }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState('');
  // The person's one access (the same link as in the Diagnostic, coaching and courses); copying never changes it
  const copyAccess = async () => {
    setNote('');
    try {
      let r = await coachCall({ action: 'personAccess', personId: d.person.id });
      if (r.never && window.confirm('Todavía no tiene link personal. ¿Crearlo ahora?')) r = await coachCall({ action: 'personAccess', op: 'create', personId: d.person.id });
      if (!r.link) setNote(r.review ? 'Acceso para revisar: decide en su ficha.' : r.revoked ? 'Acceso revocado: se renueva en su ficha.' : 'Todavía no tiene link personal.');
      else setNote((await copy(r.link)) ? 'Link personal copiado.' : r.link);
    } catch (e) { setNote(e.message); }
  };
  useEffect(() => {
    let alive = true;
    fetch('/api/coaching-admin', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ action: 'contactLinks', stakeholderId }) })
      .then(r => (r.ok ? r.json() : null)).then(x => { if (alive) setD(x); }).catch(() => {});
    return () => { alive = false; };
  }, [stakeholderId]);
  if (!d) return null;
  return (
    <div style={{ marginTop: 12, padding: 10, border: '1px solid rgba(91,191,181,0.35)', borderRadius: 8, fontSize: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>🌱 Acompañamiento <span style={mutedSt}>· solo lo ves tú, como coach</span></div>
      {!d.person ? <span style={mutedSt}>Este contacto no está vinculado con ninguna persona de Acompañamiento.</span> : (
        <div style={{ display: 'grid', gap: 4 }}>
          <div>Persona: <button type="button" className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => navigate({ page: 'coaching', personId: d.person.id })}>{d.person.name} ↗</button> <button type="button" className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={copyAccess}>🔗 Copiar link de la persona</button></div>
          {note && <span style={mutedSt}>{note}</span>}
          {!d.processes.length ? <span style={mutedSt}>Sin procesos activos.</span> : d.processes.map(p => (
            <div key={p.id}>Proceso: <button type="button" className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => navigate({ page: 'coaching', processId: p.id })}>{p.name} ↗</button> <span style={mutedSt}>{p.modality === 'team' ? 'equipo' : 'individual'} · {p.status}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}
